/* eslint-disable */
import { useEffect, useState, useRef } from 'react';
import { Stack, Box } from '@mui/material';

// UrbanMix Embedded Components
import {
  BuildingView,
  BuildingViewRef,
  EmbeddedModelLoader,
} from '@urbanmix-tech/um-embedded';

import { Building, LeaseLifecycle } from '@urbanmix-tech/shared-js';

import { Visualizer } from '@urbanmix-tech/um-embedded';
import { useEmbeddedAuth } from '@/contexts/EmbeddedAuth';
import BuildingLegend from './BuildingLegend';

const { VITE_UM_EMBEDDED_CLIENT_END_POINT } = import.meta.env;

type BuildingDisplayModelGenericProps = {
  building: Building;
  projectId: string;
  statusFilter: LeaseLifecycle[];
  apartmentData?: Array<{ apartmentId: string; value: any; label?: string }>;
  visualizer?: Visualizer;
  showLegend?: boolean;
};

export default function BuildingDisplayModelGeneric({
  building,
  projectId,
  statusFilter,
  apartmentData,
  visualizer,
  showLegend = false,
}: BuildingDisplayModelGenericProps) {
  const buildingView = useRef<BuildingViewRef | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [embeddedLoader, setEmbeddedLoader] = useState<EmbeddedModelLoader | null>(null);
  const [buildingLoaded, setBuildingLoaded] = useState(false);
  const { jwt, isLoading: jwtLoading } = useEmbeddedAuth();

  useEffect(() => {
    console.log('^^^^getting loader for portfolio preview', projectId);

    if (jwtLoading || !jwt) {
      // Wait for JWT to be available
      return;
    }

    setLoading(true);
    const loader = new EmbeddedModelLoader(
      projectId,
      jwt,
      VITE_UM_EMBEDDED_CLIENT_END_POINT
    );
    setEmbeddedLoader(loader);
    setLoading(false);
  }, [projectId, jwt, jwtLoading]);

  useEffect(() => {
    return () => {
      console.log('^^^^clean up portfolio preview for', projectId);
      setEmbeddedLoader(null);
    };
  }, []);

  // Update building data when apartmentData or visualizer changes
  useEffect(() => {
    if (buildingView.current && buildingLoaded) {
      if (apartmentData && visualizer) {
        buildingView.current.setData(apartmentData, visualizer);
      } else {
        buildingView.current.setData([], undefined);
      }
    }
  }, [apartmentData, visualizer, buildingLoaded]);

  const baseTheme = {
    type: "LiveLease",
    backgroundColor: "#09090b",
    floorColor: "#09090b", // Match background - floor becomes invisible

    // Enable transparent background so CSS gradient shows through
    // transparent: true,

    // Lighting settings
    directionalLights: [
      {
        color: "#ffffff",
        intensity: 2.5,
        castShadow: true,
      },
      {
        color: "#e8e4f0",
        intensity: 2,
        castShadow: false,
      },
    ],

    // Enable camera-attached lighting for consistent illumination as camera rotates
    attachLightsToCamera: true,

    // Auto-fit configuration - controls camera framing of model
    autoFit: {
      fillRatio: 0.85, // Fill 85% of viewport
      distanceMultiplier: 1,
      offsetMultiplier: 0.55, // 45° camera angle
      minDistance: 10,
      fallbackDistance: 50,
    },

    transparentMaterialColor: 0x808080,
    addSky: false,
    ambientIntensity: 0.5,
    envMapIntensity: 0.8,
    shadows: {
      enabled: true, autoUpdate: true
    },
    cameraControls: {
      maxDistance: 500,
      minDistance: 10,
      maxPolarAngle: Math.PI / 2,
      screenSpacePanning: true,
    },
  };

  return (
    <Stack
      justifyContent="flex-start"
      alignItems="center"
      className="h-full w-full"
    >
      {loading && <div className="text-white/50 text-sm">Loading...</div>}
      {embeddedLoader && (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <BuildingView
            ref={buildingView}
            loader={embeddedLoader}
            visualizer={visualizer}
            theme={baseTheme}
            onBuildingLoaded={(apartments) => {
              console.log('portfolio preview loaded', apartments.length, 'apartments');
              setBuildingLoaded(true);
            }}
            // No interaction handlers for display-only mode
            onApartmentSelected={() => {}}
            onApartmentHover={() => {}}
          />
          {showLegend && <BuildingLegend visualizer={visualizer} />}
        </Box>
      )}
    </Stack>
  );
}
